import { type GroupInstrumentUniverseRequest, GroupUniverseControllerApiFactory } from "@eu.mdotm.api/sphere";
import { zodResolver } from "@hookform/resolvers/zod";
import { Row } from "@mdotm/shadcn/legacy/components";
import { groupBy } from "@mdotm/utils/collections";
import { noop } from "@mdotm/utils/runtime";
import i18next from "i18next";
import { useState } from "react";
import { flushSync } from "react-dom";
import { type FieldErrors, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Redirect, useHistory } from "react-router";
import { z } from "zod";
import { getApiGen } from "$root/api/factory";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { IconWalls } from "$root/components/IconWall";
import { LeavePrompt } from "$root/components/LeavePrompt";
import { PageHeader } from "$root/components/PageHeader";
import { typedUrlForRoute, useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { spawnCreateDialog } from "$root/components/spawnable/entity-management/create-dialog";
import { UNIVERSE_INSTRUMENT_LIMIT, UNIVERSE_WITH_GROUPS_GROUP_LIMIT } from "$root/functional-areas/const";
import { getTotalAllocationWeight } from "$root/functional-areas/instruments-editor/builder";
import { universeWithGroupsRepository } from "$root/functional-areas/universe/universeWithGroupsRepository";
import { formatNumber } from "$root/localization/formatters";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { handleSubmitToCustomSubmitHandler } from "$root/third-party-integrations/react-hook-form";
import { zExt } from "$root/third-party-integrations/zod";
import { findMap } from "$root/utils";
import { Card } from "$root/widgets-architecture/layout/Card";
import { PortfolioStudioTab } from "../PortfoliosStudio/portfolio-studio-tabs";
import { decimalPlaces, step } from "./partials/editor-config";
import { UniverseWithGroupsCardContent } from "./partials/UniverseWithGroupsCardContent";
import { Button } from "@mdotm/shadcn/mdotm/components";

function TitleActions({
	onSubmit,
	onCancel,
	disableSubmit,
}: {
	onSubmit(): Promise<void>;
	onCancel(): void;
	disableSubmit?: boolean;
}) {
	const { t } = useTranslation();
	return (
		<Row flexGrow={0} childrenGrow={0} gap={16} alignItems="center" className="py-2.5">
			<Button onClick={() => onCancel()} size="small" palette="tertiary">
				{t("BUTTON.CANCEL")}
			</Button>
			<Button palette="primary" size="small" onClickAsync={() => onSubmit()} disabled={disableSubmit}>
				{t("BUTTON.SAVE")}
			</Button>
		</Row>
	);
}

export default function EditOrNewUniverseWithGroupPage({
	universeUuidOrNew,
	intentId,
	selectedGroupUuid,
}: {
	universeUuidOrNew?: string;
	intentId?: string;
	selectedGroupUuid?: string;
}): React.JSX.Element {
	if (!universeUuidOrNew) {
		return (
			<Redirect
				to={typedUrlForRoute("PortfoliosStudio", {
					tab: PortfolioStudioTab.Universes,
				})}
			/>
		);
	}

	return universeUuidOrNew === "new" ? (
		<NewUniverseWithGroup intentId={intentId} />
	) : (
		<EditUniverseWithGroup universeUuid={universeUuidOrNew} selectedGroupUuid={selectedGroupUuid} />
	);
}

function NewUniverseWithGroup({ intentId }: { intentId?: string }): React.JSX.Element {
	const { push } = useTypedNavigation();
	const crumbs = useCrumbs();
	const form = useForm<GroupInstrumentUniverseRequest>({
		defaultValues: { groups: [] },
		resolver: zodResolver(groupsValidator),
	});
	const errorMessage = extractErrorMessage(form.formState.errors);
	const { t } = useTranslation();

	return (
		<>
			<LeavePrompt
				title={t("LEAVE_PAGE")}
				when={form.formState.isDirty && !form.formState.isSubmitting && !form.formState.isSubmitSuccessful}
				pathToNotBlock={["/login"]}
			>
				{t("LEAVE_PAGE_MESSAGE")}
			</LeavePrompt>
			<PageHeader
				crumbs={[...crumbs.portfolioStudio("Universes"), ...crumbs.newUniverseWithGroups]}
				titleAction={
					<TitleActions
						onCancel={() => push("PortfoliosStudio", { tab: PortfolioStudioTab.Universes })}
						disableSubmit={!form.formState.isValid}
						onSubmit={handleSubmitToCustomSubmitHandler({
							handleSubmit: form.handleSubmit,
							baseSubmitFn: async ({ groups }) => {
								const sanitizedGroups = sanitizeGroups(groups!);

								spawnCreateDialog({
									checkIfNameIsAvailable: universeWithGroupsRepository.isUniverseNameAvailable,
									async onSubmitAsync(name) {
										const universe = await axiosExtract(
											getApiGen(GroupUniverseControllerApiFactory).createGroupUniverse({
												name,
												groups: sanitizedGroups,
											}),
										);
										trackMixPanelEvent("universe.entityActions.create", {
											universe: {
												uuid: universe.uuid!,
												name,
												type: "WITH_GROUPS",
											},
											intentId,
										});
										push("UniverseWithGroupsDetails", {
											universeUuid: universe.uuid!,
										});
									},
								}).catch(noop);
							},
						})}
					/>
				}
			/>
			<Card title={t("UNIVERSE.READ_ONLY.NAME")}>
				<UniverseWithGroupsCardContent
					mode="edit"
					groups={form.watch("groups")!}
					onChangeGroups={(groups) =>
						form.setValue("groups", groups, {
							shouldDirty: true,
							shouldValidate: true,
						})
					}
					errorMessage={errorMessage}
				/>
			</Card>
		</>
	);
}

function EditUniverseWithGroup({
	universeUuid,
	selectedGroupUuid,
}: {
	universeUuid: string;
	selectedGroupUuid?: string;
}): React.JSX.Element {
	const universeQuery = universeWithGroupsRepository.universeWithGroup.useQuery(universeUuid);

	return (
		<ReactQueryWrapperBase
			query={universeQuery}
			loadingFallback={<IconWalls.LoadingData />}
			errorFallback={<IconWalls.ErrorData />}
		>
			{(universe) => <EditUniverseWithGroupInner universe={universe} selectedGroupUuid={selectedGroupUuid} />}
		</ReactQueryWrapperBase>
	);
}

/** @internal — exported for tests */
export const groupsValidator = z.object({
	groups: z
		.array(
			z.object({
				uuid: z.any(),
				name: zExt.nonEmptyString(i18next.t),
				instruments: z.array(
					z.object({
						tickerName: z.string(),
						weight: z.number().optional().nullable(),
					} satisfies Record<
						keyof NonNullable<NonNullable<GroupInstrumentUniverseRequest["groups"]>[number]["instruments"]>[number],
						unknown
					>),
				),
			} satisfies Record<keyof NonNullable<GroupInstrumentUniverseRequest["groups"]>[number], unknown>),
		)
		.superRefine((groups, ctx) => {
			const sanitizedGroups = sanitizeGroups(groups);

			if (sanitizedGroups.length === 0) {
				ctx.addIssue({
					code: "custom",
					path: ["hidden"],
					message: "Add at least one instrument to your composition",
				});
			}

			const groupWithInvalidSum = findMap(sanitizedGroups, (group) => {
				const sum = getTotalAllocationWeight(group.instruments.map((instr) => instr.weight ?? 0)).toNumber();
				const hasInvalidSum = Math.abs(sum - 100) >= step;
				if (hasInvalidSum) {
					return [{ name: group.name, sum, uuid: group.uuid }];
				}
				return [];
			});

			if (groupWithInvalidSum) {
				ctx.addIssue({
					code: "custom",
					path: [],
					message: `Group "${groupWithInvalidSum.name}" has a total weight of ${formatNumber(
						groupWithInvalidSum.sum,
						decimalPlaces,
					)}%`,
				});
			}

			if (sanitizedGroups.length > UNIVERSE_WITH_GROUPS_GROUP_LIMIT) {
				ctx.addIssue({
					code: "custom",
					path: [],
					message: `The number of groups exceeds the limit of ${UNIVERSE_WITH_GROUPS_GROUP_LIMIT}, please remove some groups to continue`,
				});
			}

			const byName = groupBy(sanitizedGroups, (g) => g.name.toLowerCase().trim());
			for (const groupsWithSameName of Object.values(byName)) {
				if (groupsWithSameName && groupsWithSameName.length > 1) {
					ctx.addIssue({
						code: "custom",
						path: [],
						message: `Multiple groups named "${groupsWithSameName[0].name}" exist`,
					});
				}
			}

			const instrumentSet = new Set(sanitizedGroups.flatMap((g) => g.instruments.flatMap((instr) => instr.tickerName)));
			if (instrumentSet.size > UNIVERSE_INSTRUMENT_LIMIT) {
				ctx.addIssue({
					code: "custom",
					path: [],
					message: `The number of unique instruments exceeds the limit of ${UNIVERSE_INSTRUMENT_LIMIT}, please remove some instruments to continue`,
				});
			}
		}),
	name: z.any(),
	uuid: z.any(),
} satisfies Record<keyof NonNullable<GroupInstrumentUniverseRequest>, unknown>);

function EditUniverseWithGroupInner({
	universe,
	selectedGroupUuid,
}: {
	universe: GroupInstrumentUniverseRequest;
	selectedGroupUuid?: string;
}) {
	const { push } = useTypedNavigation();
	const crumbs = useCrumbs();
	const form = useForm<GroupInstrumentUniverseRequest>({
		defaultValues: universe,
		resolver: zodResolver(groupsValidator),
	});
	const errorMessage = extractErrorMessage(form.formState.errors);
	const { t } = useTranslation();

	const history = useHistory();
	const [pathToNotBlock, setPathToNotBlock] = useState<string[]>(["/login"]);

	return (
		<>
			<LeavePrompt
				title={t("LEAVE_PAGE")}
				when={form.formState.isDirty && !form.formState.isSubmitting}
				pathToNotBlock={pathToNotBlock}
			>
				{t("LEAVE_PAGE_MESSAGE")}
			</LeavePrompt>
			<PageHeader
				crumbs={[
					...crumbs.portfolioStudio("Universes"),
					...crumbs.universeName({ uuid: universe.uuid, name: universe.name }),
				]}
				titleAction={
					<TitleActions
						onCancel={() => history.goBack()}
						disableSubmit={!form.formState.isValid}
						onSubmit={() =>
							form.handleSubmit(async ({ groups }) => {
								const sanitizedGroups = sanitizeGroups(groups!);

								await getApiGen(GroupUniverseControllerApiFactory).updateUniverse({
									uuid: universe.uuid,
									name: universe.name,
									groups: sanitizedGroups,
								});
								flushSync(() =>
									setPathToNotBlock((latestPath) => [
										...latestPath,
										typedUrlForRoute("UniverseWithGroupsDetails", {
											universeUuid: universe.uuid!,
										}),
									]),
								);
								push("UniverseWithGroupsDetails", {
									universeUuid: universe.uuid!,
								});
							})()
						}
					/>
				}
			/>
			<Card title={t("UNIVERSE.READ_ONLY.NAME")}>
				<UniverseWithGroupsCardContent
					mode="edit"
					groups={form.watch("groups")!}
					onChangeGroups={(groups) =>
						form.setValue("groups", groups, {
							shouldDirty: true,
							shouldValidate: true,
						})
					}
					errorMessage={errorMessage}
					initialSelectedGroupUuid={selectedGroupUuid}
				/>
			</Card>
		</>
	);
}
/** @internal — exported for tests */
export function extractErrorMessage(errors: FieldErrors<GroupInstrumentUniverseRequest>): string | undefined {
	return (
		[
			...(errors.groups?.message ? [errors.groups.message] : []),
			...(errors.groups?.flatMap?.((err) => [
				...(err?.message ? [err.message] : []),
				...(err?.instruments?.message ? [err.instruments.message] : []),
				...(err?.instruments?.flatMap?.((instr) => (instr?.message ? [instr.message] : [])) ?? []),
			]) ?? []),
		].join("\n") || undefined
	);
}

export function sanitizeGroups<
	G extends Array<{
		name?: string;
		instruments?: Array<{ weight?: number | null }>;
	}>,
>(groups: G): G {
	const nameWithoutCounterMap = new Map<string, number>();
	const existingNames = new Set<string>();
	return groups
		.slice()
		.reverse() // note: `reverse` because we prepend new groups, so the naming remains consistent when editing. We reverse the array again before returning
		.flatMap((g) => {
			const sanitizedInstruments =
				g.instruments?.flatMap((instr) =>
					instr.weight != null && instr.weight > 0 ? [{ ...instr, weight: instr.weight }] : [],
				) ?? [];

			if (sanitizedInstruments.length > 0) {
				const sanitizedName = (g.name || "Untitled").match(/^(.+?)(\s\(\d+\))?$/)![1];
				const nameKey = sanitizedName.toLowerCase().trim();
				const counter = (nameWithoutCounterMap.get(nameKey) ?? 0) + 1;
				let found = null;
				while (!found) {
					const candidate = counter === 1 ? sanitizedName : `${sanitizedName} (${counter})`;
					const normalizedCandidate = candidate.toLowerCase().trim();
					if (!existingNames.has(normalizedCandidate)) {
						existingNames.add(normalizedCandidate);
						found = candidate;
					}
				}
				nameWithoutCounterMap.set(nameKey, counter);
				return [
					{
						...g,
						name: found,
						instruments: sanitizedInstruments,
					},
				];
			}
			return [];
		})
		.reverse() as typeof groups;
}
